/* eslint-disable @typescript-eslint/no-explicit-any */
import { Decorator, FormApi, getIn } from 'final-form';
import { clone, equals, omit } from 'ramda';

import {
  ARRAY_ITEM_TEXT_FIELDS,
  SYNC_DIRECTIONS,
  SYNC_GROUPS,
  TEXT_FIELDS,
  TEXT_SYNC_DELAY_MS,
  VIOLATION_COPIES_CONTAINERS,
  ViolationCopyOwnerType,
} from './config';
import { SilentValueChange } from './mutators';

interface IViolationCopy {
  key: string;
  ownerType: ViolationCopyOwnerType;
  path: string;
  violationId: string;
  value: any;
}

/**
 * violationId -> поле -> ключ копии, из которой переносим.
 * Источник хранится на поле, чтобы правки одного нарушения в разных решениях до flush не затирали друг друга.
 */
type PendingSync = Map<string, Map<string, string>>;

const flushByForm = new WeakMap<FormApi<any, any>, () => void>();

/**
 * Сразу применяет отложенный перенос.
 * Нужно вызвать перед тем, как значения формы уходят на бек в обход submit.
 */
export const flushSharedViolationsSync = (form: FormApi<any, any> | null | undefined) => {
  if (form) flushByForm.get(form)?.();
};

const isDirectionAllowed = (from: ViolationCopyOwnerType, to: ViolationCopyOwnerType) =>
  SYNC_DIRECTIONS.some(direction => direction.from === from && direction.to === to);

const normalize = (value: unknown) => (value === undefined ? null : value);

/**
 * Собирает копии нарушений по значениям формы, а не по отрисованным полям, поэтому видит и нераскрытые решения.
 * Ключ строится по guid решения, чтобы удаление или перестановка решений не выглядели как правка нарушения.
 */
const collectCopies = (values: any): Map<string, IViolationCopy> => {
  const copies = new Map<string, IViolationCopy>();

  VIOLATION_COPIES_CONTAINERS.forEach(({ ownerType, decisionsPath, requirementsKey }) => {
    const decisions = getIn(values, decisionsPath);

    if (!Array.isArray(decisions)) return;

    decisions.forEach((decision: any, decisionIndex: number) => {
      const decisionKey = decision?.guid || `#${decisionIndex}`;

      decision?.[requirementsKey]?.forEach((npa: any, npaIndex: number) => {
        npa?.violatedMandatory?.forEach((mandatory: any, mandatoryIndex: number) => {
          mandatory?.violations?.forEach((violation: any, violationIndex: number) => {
            if (!violation?.id) return;

            const path = `${decisionsPath}[${decisionIndex}].${requirementsKey}[${npaIndex}].violatedMandatory[${mandatoryIndex}].violations[${violationIndex}]`;
            const baseKey = `${decisionsPath}|${decisionKey}|${violation.id}`;
            const key = copies.has(baseKey) ? `${baseKey}|${path}` : baseKey;

            copies.set(key, { key, ownerType, path, violationId: violation.id, value: violation });
          });
        });
      });
    });
  });

  return copies;
};

const isContainersChanged = (values: any, prevValues: any) =>
  VIOLATION_COPIES_CONTAINERS.some(
    ({ decisionsPath }) => getIn(values, decisionsPath) !== getIn(prevValues, decisionsPath),
  );

// Изменился только текст, такое переносим по таймеру, а не на каждое нажатие
const isTextOnlyChange = (field: string, prevValue: any, value: any) => {
  if (TEXT_FIELDS.includes(field)) return true;

  const itemTextFields = ARRAY_ITEM_TEXT_FIELDS[field];

  if (!itemTextFields || !Array.isArray(prevValue) || !Array.isArray(value) || prevValue.length !== value.length) {
    return false;
  }

  return value.every((item, index) =>
    equals(omit(itemTextFields, item ?? {}), omit(itemTextFields, prevValue[index] ?? {})),
  );
};

/**
 * Переносит правки нарушения из одного решения во все остальные решения с этим нарушением.
 * На беке копии одного нарушения это одна запись и сохраняется последняя копия, поэтому копии в форме должны совпадать.
 * Запись тихая, через мутатор setValuesSilent, чтобы не снимать подпись с других предписаний.
 */
export const sharedViolationsSyncDecorator: Decorator<any, any> = form => {
  let isFirstNotification = true;
  let prevValues: any;
  let prevInitialValues: any;
  let prevActive: string | undefined;
  let copies = new Map<string, IViolationCopy>();
  let isApplying = false;
  let immediateTimer: ReturnType<typeof setTimeout> | undefined;
  let textTimer: ReturnType<typeof setTimeout> | undefined;
  const pending: PendingSync = new Map();

  const clearTimers = () => {
    if (immediateTimer) clearTimeout(immediateTimer);
    if (textTimer) clearTimeout(textTimer);
    immediateTimer = undefined;
    textTimer = undefined;
  };

  const flush = () => {
    clearTimers();

    if (!pending.size) return;

    const currentCopies = collectCopies(form.getState().values);
    const changes: SilentValueChange[] = [];

    pending.forEach((fields, violationId) => {
      fields.forEach((sourceKey, field) => {
        const source = currentCopies.get(sourceKey);

        if (!source) return;

        const sourceValue = source.value?.[field];

        currentCopies.forEach(target => {
          if (
            target.violationId !== violationId ||
            target.key === sourceKey ||
            !isDirectionAllowed(source.ownerType, target.ownerType) ||
            equals(normalize(target.value?.[field]), normalize(sourceValue))
          ) {
            return;
          }

          // Глубокая копия, чтобы решения не делили одни и те же объекты
          changes.push([`${target.path}.${field}`, clone(sourceValue)]);
        });
      });
    });

    pending.clear();

    if (!changes.length) return;

    isApplying = true;

    try {
      form.mutators.setValuesSilent(changes);
    } finally {
      isApplying = false;
    }
  };

  // Не применяем перенос внутри оповещения формы, только следующим тактом
  const scheduleImmediate = () => {
    if (!immediateTimer) immediateTimer = setTimeout(flush, 0);
  };

  const scheduleText = () => {
    if (textTimer) clearTimeout(textTimer);
    textTimer = setTimeout(flush, TEXT_SYNC_DELAY_MS);
  };

  const reset = (values: any) => {
    pending.clear();
    clearTimers();
    copies = collectCopies(values);
  };

  const handleValuesChange = (values: any) => {
    const nextCopies = collectCopies(values);
    const copiesCount = new Map<string, number>();
    let hasImmediate = false;
    let hasText = false;

    nextCopies.forEach(copy => copiesCount.set(copy.violationId, (copiesCount.get(copy.violationId) || 0) + 1));

    nextCopies.forEach(copy => {
      const prevCopy = copies.get(copy.key);

      if (!prevCopy || prevCopy.value === copy.value || (copiesCount.get(copy.violationId) || 0) < 2) return;

      // Копию целиком заменили, например перезаполнили решение из акта, это не правка пользователя
      if (prevCopy.value?.isNew !== copy.value?.isNew) return;

      Object.values(SYNC_GROUPS).forEach(fields => {
        const changedFields = fields.filter(field => !equals(prevCopy.value?.[field], copy.value?.[field]));

        if (!changedFields.length) return;

        if (!pending.has(copy.violationId)) pending.set(copy.violationId, new Map());

        const violationPending = pending.get(copy.violationId)!;

        // Изменённые поля берём из этой копии, остальные поля группы только если их ещё не ждут из другой копии
        fields.forEach(field => {
          if (changedFields.includes(field) || !violationPending.has(field)) {
            violationPending.set(field, copy.key);
          }
        });

        changedFields.forEach(field => {
          if (isTextOnlyChange(field, prevCopy.value?.[field], copy.value?.[field])) {
            hasText = true;
          } else {
            hasImmediate = true;
          }
        });
      });
    });

    copies = nextCopies;

    if (hasImmediate) {
      scheduleImmediate();
    } else if (hasText) {
      scheduleText();
    }
  };

  const unsubscribe = form.subscribe(
    ({ values, initialValues, active }) => {
      const isActiveChanged = active !== prevActive;
      const isInitialValuesChanged = initialValues !== prevInitialValues;

      prevActive = active as string | undefined;

      // Загрузка или переинициализация после сохранения, запоминаем снимок и ничего не переносим
      if (isFirstNotification || isInitialValuesChanged) {
        isFirstNotification = false;
        prevInitialValues = initialValues;
        prevValues = values;
        reset(values);

        return;
      }

      if (values !== prevValues) {
        const isChanged = isContainersChanged(values, prevValues);

        prevValues = values;

        if (isApplying) {
          copies = collectCopies(values);
        } else if (isChanged) {
          handleValuesChange(values);
        }
      }

      // Поле потеряло фокус, отложенный текст переносим не дожидаясь таймера
      if (isActiveChanged && textTimer) scheduleImmediate();
    },
    { values: true, initialValues: true, active: true },
  );

  const originalSubmit = form.submit;

  // Подменяем submit, чтобы отложенный перенос успел до отправки
  // eslint-disable-next-line no-param-reassign
  form.submit = () => {
    flush();

    return originalSubmit();
  };

  flushByForm.set(form, flush);

  return () => {
    unsubscribe();
    clearTimers();
    // eslint-disable-next-line no-param-reassign
    form.submit = originalSubmit;
    flushByForm.delete(form);
  };
};
